import { useMockData } from "../constants/environment";
import { School } from "../models/school/school";
import { getSchoolsLocalized } from "../mock-data/school/schools-mock-data";
import { CacheUtil } from "../utils/cache-util";
import { Language } from "../constants/language";
import {PaginationUtil, PaginatedResult, PAGINATION} from "../utils/pagination-util";

export interface SchoolFilter {
  category?: string[];
  featured?: boolean;
  hasSchedule?: boolean;
  [key: string]: any;
}

export interface SchoolSortOption {
  field: keyof School | string;
  direction: 'asc' | 'desc';
}

class SchoolService {

  private static readonly SCHOOL_DATA_CACHE_KEY = "schoolContent";

  public async getSchools(
    language: Language = Language.AR,
    filter?: SchoolFilter,
    sort?: SchoolSortOption,
    page: number = PAGINATION.DEFAULT_PAGE,
    limit: number = PAGINATION.DEFAULT_PAGE_SIZE,
    fetchAll: boolean = false
  ): Promise<PaginatedResult<School>> {

    let schools: School[] = [];
    let paginatedResult: PaginatedResult<School>;

    const cacheKey = `${SchoolService.SCHOOL_DATA_CACHE_KEY}_${language}`;
    const cachedData = CacheUtil.get<School[]>(cacheKey);
    if (cachedData) {
      schools = cachedData;
      paginatedResult = PaginationUtil.paginate<School>(schools, page, limit);
    }
    else {
      if (useMockData) {
        await new Promise(resolve => setTimeout(resolve, 300));

        paginatedResult = getSchoolsLocalized(language, true);
        schools = paginatedResult.data;
        CacheUtil.set(cacheKey, schools);
      } else {
        //TODO USE API CALL TO FETCH REAL DATA
        paginatedResult = getSchoolsLocalized(language, true);
        schools = paginatedResult.data;
        CacheUtil.set(cacheKey, schools);
      }
    }

    if (filter) schools = this.applyFilters(schools, filter);
    if (sort)   schools = this.applySorting(schools, sort);

    if(filter || sort) paginatedResult = PaginationUtil.paginate<School>(schools, page, limit); //Update Pagination if filtering or sorting applied
    return paginatedResult;
  }

  public async getSchoolById(id: number, language: Language = Language.AR): Promise<School | undefined> {
    const schoolResults = await this.getSchools(language);
    const schools: School[] = schoolResults.data ? schoolResults.data : [];
    return schools.find(school => school.id === id);
  }

  public async getSchoolByKey(key: string, language: Language = Language.AR): Promise<School | undefined> {
    const schoolResults = await this.getSchools(language);
    const schools: School[] = schoolResults.data ? schoolResults.data : [];
    return schools.find(school => school.key === key);
  }

  public async getFeaturedSchools(language: Language = Language.AR, limit: number = PAGINATION.DEFAULT_PAGE_SIZE): Promise<School[]> {
    const schoolResults = await this.getSchools(language);
    const schools: School[] = schoolResults.data ? schoolResults.data : [];
    return schools.slice(0, limit);
  }

  private applyFilters(schools: School[], filter: SchoolFilter): School[] {
    return schools.filter(school => {
      if (filter.category && filter.category.length > 0) {
        if (!school.category || !filter.category.includes(school.category)) return false;
      }

      if (filter.hasSchedule !== undefined) {
        const hasSchedule = school.startDate && school.endDate;
        if (!!hasSchedule !== filter.hasSchedule) return false;
      }

      return true;
    });
  }

  private applySorting(schools: School[], sort: SchoolSortOption): School[] {
    return [...schools].sort((a, b) => {
      let aValue: any = a[sort.field as keyof School];
      let bValue: any = b[sort.field as keyof School];

      // Handle string fields
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (sort.direction === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });
  }
}

export const schoolService = new SchoolService();